import { v4 as uuid } from 'uuid';
import { genDay } from '@onward-delivery/core';
import { asDateInTZ, asUTCDate } from '@/utilities/convertToISO';
import { resourceDayState } from '@/utilities/resourceDayState';
import { ROUTE_COLORS, TEMP_ROUTE } from './constants';

// Assignment status comes from pickup_route_id / dropoff_route_id which the
// planning context injects on orders resolved from the `routes` query
// (assigned) but not on orders from `pickups` / `dropoffs` (unassigned).
export const isOrderAssigned = (o) => (o?.crossdock_leg === 'pickup' ? !!o.pickup_route_id : !!o.dropoff_route_id);

const EMPTY_ARRAY = [];

// routes has one partner slot, carrier_id, and no contractor column: a subcontracted route carries the
// contractor's client_id there.
export const routePartner = (route, { partner_carriers, partner_contractors } = {}) => {
    const partnerId = route?.carrier_id;
    if (!partnerId) return null;

    return (
        (partner_carriers || []).find((p) => p?.carrier_id === partnerId)?.carrier ||
        (partner_contractors || []).find((p) => p?.contractor_id === partnerId)?.contractor ||
        null
    );
};

// Contractor-only: a carrier-type partnership would mean handing final mile to a wholly different
// carrier, which planning no longer supports (see partnerAssignmentUpdates) — carrier_id never
// changes here, only contractor_id. Still deduped by client_id in case of duplicate rows.
export const routePartnerOptions = ({ partner_contractors } = {}) =>
    (partner_contractors || []).reduce((acc, p) => {
        if (p?.contractor_id && p?.contractor && !acc.some((kept) => kept.client_id === p.contractor_id)) {
            acc.push({
                client_id: p.contractor_id,
                business_name: p.contractor.business_name,
                partner_type: 'contractor',
            });
        }

        return acc;
    }, []);

// One client can be both a carrier partner and a contractor partner of the same flex center, so an
// ambiguous client_id resolves to no leg rather than to a guess.
export const partnerTypeFor = (options, clientId) => {
    const matches = (options || []).filter((option) => option?.client_id === clientId);

    return matches.length === 1 ? matches[0].partner_type : null;
};

// Contractor-only: carrier_id is never written here, so there is nothing to reverse a full
// carrier takeover back to. UPSERT_ROUTE's `update_columns: [contractor_id]` becomes
// `SET contractor_id = EXCLUDED.contractor_id` — every update names it, whether setting or clearing.
export const partnerAssignmentUpdates = ({ orders, partnerId, partnerType, userId }) => {
    if (partnerId && partnerType !== 'contractor') return [];

    return (orders || []).reduce((acc, { order }) => {
        if (!order) return acc;

        if (!partnerId) {
            if (!order.contractor_id) return acc;

            acc.push({ order_id: order.order_id, contractor_id: null });

            return acc;
        }

        // Only a final mile this user actually holds can be subcontracted.
        if (order.carrier_id !== userId) return acc;

        acc.push({ order_id: order.order_id, contractor_id: partnerId });

        return acc;
    }, []);
};

// Must start at BUSINESS-timezone midnight: that's the zone Resources/store.js writes rule and
// override bounds in, and core's overlaps() requires range[0] <= ref[0], so a UTC-midnight window
// sorts before a same-day override and drops it. Core reads a day's date in UTC (170.0.0), which
// business midnight in every US zone still lands on.
const businessDayWindow = (dateish, tz) => {
    const d = new Date(dateish);
    if (Number.isNaN(d.getTime())) return null;

    const utcMidnight = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString();

    return genDay(asDateInTZ(utcMidnight, tz).toISOString());
};

// Whether the resource will take new work that day. Separate from isResourceScheduled because a
// route can point at a resource that has since been closed: the route still exists and still shows,
// but nothing new may be assigned to it. Defaults to true so a missing resource or an unparseable
// date never blocks an assign on its own.
export const isResourceAcceptingBookings = (resource, dateish, tz) => {
    if (!resource || !dateish) return true;

    const day = businessDayWindow(dateish, tz);
    if (!day) return true;

    return resourceDayState(resource, day).accepting;
};

// Whether the resource works that day at all, open to new bookings or not.
//
// Not core's scheduleResource(): it returns !omit, so a closed day read as though the resource did
// not exist — no shell route, and its already-assigned orders had nowhere to go.
export const isResourceScheduled = (resource, dateish, tz) => {
    if (!resource || !dateish) return false;

    const day = businessDayWindow(dateish, tz);
    if (!day) return false;

    return resourceDayState(resource, day).scheduled;
};

// The stops panel's resource picker. Only resources scheduled on the route's day are listed — one
// not working that day is not a choice at all. Picking one rewrites resource_id on every order on
// the route, a new booking, so a day closed to bookings is left out too, except for the route's
// current resource: re-listing it books nothing new.
export const selectableResources = (resources, route, tz) => {
    if (!route?.scheduled_delivery || !resources) return [];

    return resources.filter(
        (r) =>
            isResourceScheduled(r, route.scheduled_delivery, tz) &&
            (r.resource_id === route.resource_id || isResourceAcceptingBookings(r, route.scheduled_delivery, tz))
    );
};

// Derives the combined order-row list (routed + unassigned) from the planning
// query result. Rows are accumulated into one mutable map keyed by
// `${order_id}_${crossdock_leg}` — later assignments to the same rowId win,
// preserving the legacy routes → pickups → dropoffs merge precedence.
//
// `prevRowCache` (Map rowId -> { source, pickup_route_id, dropoff_route_id,
// crossdock_leg, isMissed, row }) enables row identity reuse across
// recomputes: when the source cache entity reference and every injected field
// are unchanged, the previous row object is returned as-is. Source comparison
// is strictly referential — Apollo result caching keeps unchanged entities
// referentially stable across broadcasts, and a changed entity must always
// produce a fresh row.
export const deriveOrderRows = ({ data, deliveryDate, prevRowCache }) => {
    const descriptors = {};

    for (const route of data?.routes || []) {
        for (const mapping of route?.orders || []) {
            const source = mapping.order;
            let pickup_route_id;
            let dropoff_route_id;
            let crossdock_leg;
            if (mapping.type === 'PICKUP') {
                pickup_route_id = route.route_id;
                crossdock_leg = 'pickup';
            } else if (mapping.type === 'DROPOFF') {
                dropoff_route_id = route.route_id;
                crossdock_leg = 'dropoff';
            } else if (mapping.type === 'FULL') {
                dropoff_route_id = route.route_id;
            }
            const effectiveLeg = crossdock_leg !== undefined ? crossdock_leg : source?.crossdock_leg;
            descriptors[`${source?.order_id}_${effectiveLeg}`] = {
                source,
                pickup_route_id,
                dropoff_route_id,
                crossdock_leg,
            };
        }
    }

    for (const source of data?.pickups || []) {
        descriptors[`${source?.order_id}_pickup`] = {
            source,
            pickup_route_id: undefined,
            dropoff_route_id: undefined,
            crossdock_leg: 'pickup',
        };
    }

    for (const source of data?.dropoffs || []) {
        // Crossdock orders in pre-dropoff lifecycle states need a dropoff-leg
        // assignment; tag them so the rowId discriminates them from E2E orders
        // in the same dropoffs list. Orders already on a DROPOFF/FULL mapping
        // are filtered out upstream by the query.
        const crossdock_leg = ['CD_PENDING_PO', 'CD_PENDING', 'CD_RECEIVED'].includes(source?.event_state)
            ? 'dropoff'
            : undefined;
        const effectiveLeg = crossdock_leg !== undefined ? crossdock_leg : source?.crossdock_leg;
        descriptors[`${source?.order_id}_${effectiveLeg}`] = {
            source,
            pickup_route_id: undefined,
            dropoff_route_id: undefined,
            crossdock_leg,
        };
    }

    // "Missed" rows: unrouted orders whose leg date falls before the plan day.
    // The 30-day floor is enforced by the query's widened date window, so
    // anything earlier than the plan day that comes back here is in-window.
    // Routed orders (assigned via the routes bucket) are never missed.
    const startCutoff = asUTCDate(deliveryDate);
    const rowCache = new Map();
    const rows = Object.entries(descriptors).map(([rowId, descriptor]) => {
        const { source, pickup_route_id, dropoff_route_id, crossdock_leg } = descriptor;
        // Effective (merged) values: injected fields override the raw cache
        // object's, exactly like the legacy spread order.
        const effectiveLeg = crossdock_leg !== undefined ? crossdock_leg : source?.crossdock_leg;
        const effectivePickupRouteId = pickup_route_id !== undefined ? pickup_route_id : source?.pickup_route_id;
        const effectiveDropoffRouteId = dropoff_route_id !== undefined ? dropoff_route_id : source?.dropoff_route_id;
        const legDate = effectiveLeg === 'pickup' ? source?.pickup_date : source?.delivery_date;
        const isAssigned = effectiveLeg === 'pickup' ? !!effectivePickupRouteId : !!effectiveDropoffRouteId;
        const isMissed = !isAssigned && !!legDate && asUTCDate(legDate) < startCutoff;

        const prev = prevRowCache?.get(rowId);
        let row;
        if (
            prev &&
            prev.source === source &&
            prev.pickup_route_id === pickup_route_id &&
            prev.dropoff_route_id === dropoff_route_id &&
            prev.crossdock_leg === crossdock_leg &&
            prev.isMissed === isMissed
        ) {
            row = prev.row;
        } else {
            row = { ...source };
            if (pickup_route_id !== undefined) {
                row.pickup_route_id = pickup_route_id;
            }
            if (dropoff_route_id !== undefined) {
                row.dropoff_route_id = dropoff_route_id;
            }
            if (crossdock_leg !== undefined) {
                row.crossdock_leg = crossdock_leg;
            }
            if (isMissed) {
                row._isMissed = true;
            }
        }

        rowCache.set(rowId, { source, pickup_route_id, dropoff_route_id, crossdock_leg, isMissed, row });
        return row;
    });

    return { rows, routes: data?.routes || EMPTY_ARRAY, rowCache };
};

// Shell route_ids are real uuids: the first assign inserts the client-minted id as the DB route_id
// and removal upserts it as status 'deleted', so ids must be unique per resource per plan date.
// `shellIds` and `prevShells` are mutated in place to keep ids and object identity stable across
// recomputes.
//
// A deleted route keeps its resource off the board — deleting is how a planner takes it off — unless
// the resource is in `awaitingRoute` (orders booked onto it that no live route carries; see
// ordersOffResource.resourcesAwaitingRoute). Then it gets a shell again, so those orders have a
// route to go on.
export const deriveShellRoutes = ({
    routes,
    availableResources,
    awaitingRoute = [],
    shellIds,
    prevShells,
    shipper_id,
    deliveryDate,
    preferences_next_day_returns,
    tz,
}) => {
    // A live route consumes the resource's cached uuid, and so does a deleted one persisted under
    // it — the next shell must mint fresh so the routes_pkey upserts never see a reused id. Any
    // other deleted route leaves it alone: a returning shell sits beside that route on every
    // recompute and must keep one id throughout.
    for (const route of routes || []) {
        if (route.status !== 'deleted' || shellIds.get(route.resource_id) === route.route_id) {
            shellIds.delete(route.resource_id);
            prevShells.delete(route.resource_id);
        }
    }

    const awaiting = new Set(awaitingRoute);
    const shellResources = (availableResources || []).filter((r) => {
        const resourceRoutes = (routes || []).filter((route) => route.resource_id === r.resource_id);

        return resourceRoutes.length === 0
            ? true
            : awaiting.has(r.resource_id) && resourceRoutes.every((route) => route.status === 'deleted');
    });

    const displayedRouteColors = new Set((routes || []).map((route) => route.route_color));
    const allowedRouteColors = new Set(ROUTE_COLORS);

    // pick colors not already assigned
    const availableColors = [...new Set([...allowedRouteColors].filter((color) => !displayedRouteColors.has(color)))];

    const { route_id, route_number, ...rest } = TEMP_ROUTE;
    const scheduled_delivery = asDateInTZ(deliveryDate, tz).toISOString();

    const shellCache = new Map();
    const shells = shellResources.map((resource, i) => {
        // Get-or-create so a StrictMode double invoke reuses the first mint.
        if (!shellIds.has(resource.resource_id)) {
            shellIds.set(resource.resource_id, uuid());
        }
        const id = shellIds.get(resource.resource_id);
        const route_color = availableColors[i % availableColors.length];

        const prev = prevShells.get(resource.resource_id);
        if (
            prev &&
            prev.sourceResource === resource &&
            prev.shell.route_id === id &&
            prev.shell.route_color === route_color &&
            prev.shell.scheduled_delivery === scheduled_delivery &&
            prev.shell.finish_returns_next_day === preferences_next_day_returns &&
            prev.shell.shipper_id === shipper_id &&
            prev.shell.resource_id === resource.resource_id
        ) {
            shellCache.set(resource.resource_id, prev);
            return prev.shell;
        }

        const shell = {
            ...rest,
            shipper_id,
            scheduled_delivery,
            route_color,
            finish_returns_next_day: preferences_next_day_returns,
            status: 'planning',
            source_form: 'PLANNING',
            need_to_optimize: true,
            planning: true,
            stopsByRouteId: [],
            orders: [],
            resource_id: resource.resource_id,
            resource: {
                ...resource,
            },
            __typename: 'routes',
            route_id: id,
            // Null, not the resource name — this object is persisted on the first assign, and a
            // copied name would never follow the resource afterwards.
            route_alias: null,
            shell: true,
        };
        shellCache.set(resource.resource_id, { shell, sourceResource: resource });
        return shell;
    });

    return { shells, shellCache };
};
